import type { TypedAnalyticsContexts } from '@/analytics/snowplow/analytics-context.types';
import { useAnalyticsTracking } from '@/analytics/snowplow/AnalyticsTrackingContext';
import { useAnalyticsFeatureFlags } from '@/analytics/snowplow/hooks/useAnalyticsFeatureFlags';
import { useEmitUiEvent } from '@/analytics/snowplow/useEmitUIEvent';
import { buildContextId } from '@/analytics/snowplow/utils/buildContextId';
import { useCallback } from 'react';

export const useConsultationTracking = () => {
  const emitEvent = useEmitUiEvent();
  const { uiEvent, flags } = useAnalyticsTracking();
  const contextId = uiEvent?.context_id;
  const analyticsFlags = useAnalyticsFeatureFlags();

  const trackCompleteConsultation = useCallback(
    (consultationId: number, _extraContext?: TypedAnalyticsContexts) => {
      emitEvent(
        {
          component_name: 'CompleteConsult',
          event_name: 'complete_consult',
          id: 'complete_consult',
          // Workaround to ensure context_id and flags are included when completing a consultation via "Awaiting Documents" flow.
          // This complete confirmation path originates from Patient.tsx (not ConsultationTab), so context_id and flags will be missing
          // if the component is not wrapped with AnalyticsTrackingProvider.
          // If contextId is unavailable, fallback to a default "consultation_tab" context. This is a temporary fix.
          // Ideally we have ConsultationTab logic in ConsultationTab, and this would not be needed.
          context_id: contextId ?? buildContextId('consultation_tab'),
        },
        {
          consultation: {
            consultation_id: String(consultationId),
          },
          ...(flags?.flags ? {} : { flags: { flags: analyticsFlags } }),
        },
      );
    },
    [emitEvent, contextId, flags, analyticsFlags],
  );

  return {
    trackCompleteConsultation,
  };
};
